import { graphConfig } from '@/config/authConfig';
import { M365User } from '@/types';

/**
 * Fetches user profile data from Microsoft Graph API
 * @param accessToken - The access token obtained from MSAL
 * @returns User profile data
 */
export async function fetchGraphUserData(accessToken: string): Promise<M365User> {
  const headers = new Headers();
  headers.append('Authorization', `Bearer ${accessToken}`);

  const options = {
    method: 'GET',
    headers: headers,
  };

  try {
    const response = await fetch(graphConfig.graphMeEndpoint, options);
    if (!response.ok) {
      throw new Error(`Failed to fetch user data: ${response.statusText}`);
    }
    const data = await response.json();
    return data as M365User;
  } catch (error) {
    console.error('Error fetching user data from Microsoft Graph:', error);
    throw error;
  }
}

/**
 * Fetches user profile photo from Microsoft Graph API
 * @param accessToken - The access token obtained from MSAL
 * @returns Base64 encoded photo URL or null
 */
export async function fetchGraphUserPhoto(accessToken: string): Promise<string | null> {
  const headers = new Headers();
  headers.append('Authorization', `Bearer ${accessToken}`);

  const options = {
    method: 'GET',
    headers: headers,
  };

  try {
    const response = await fetch(graphConfig.graphPhotoEndpoint, options);
    if (!response.ok) {
      // User might not have a photo
      return null;
    }
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('User photo not available:', error);
    return null;
  }
}

/**
 * Maps M365 user data to application User format
 * @param m365User - Microsoft Graph user data
 * @param photoUrl - Optional photo URL
 * @returns Mapped User object
 */
export function mapM365UserToAppUser(m365User: M365User, photoUrl?: string | null) {
  return {
    id: m365User.id,
    name: m365User.displayName,
    email: m365User.mail || m365User.userPrincipalName,
    designation: m365User.jobTitle || 'Employee',
    department: m365User.department || 'General',
    avatar: photoUrl || undefined,
    employeeId: m365User.id,
    joiningDate: new Date().toISOString().split('T')[0], // Current date as fallback
    phone: m365User.mobilePhone || m365User.businessPhones?.[0],
    location: m365User.officeLocation || 'Remote',
    status: 'active' as const,
    // M365 specific fields
    azureId: m365User.id,
    jobTitle: m365User.jobTitle,
    officeLocation: m365User.officeLocation,
    mobilePhone: m365User.mobilePhone,
    businessPhones: m365User.businessPhones,
    preferredLanguage: m365User.preferredLanguage,
  };
}
